import { useCallback, useMemo } from 'react';
import { LiveVideoService } from './LiveVideoService';

/**
 *  Given and org and a call, do administrative live video tasks: create and
 *  destroy video sessions, create tokens for participants
 */
export interface LiveVideoSetupData {
  getDispatcherToken: () => Promise<string>;
  getCallerToken: () => Promise<string>;
  endRoom: (roomName: string) => Promise<void>;
}

const failure = async () => {
  throw 'Cannot use live video for call with missing callID or orgName';
};

const failedCallData = {
  getDispatcherToken: failure,
  getCallerToken: failure,
  endRoom: failure,
};

export const useLiveVideoSetup = ({
  callID,
  orgName,
}: {
  callID: string | null;
  orgName: string | undefined;
}): LiveVideoSetupData => {
  // We'll handle the missing callID/orgName case at the end, to obey hook laws.
  const presentCallID = callID || '';
  const presentOrgName = orgName || '';

  const api = useMemo(
    () => new LiveVideoService(presentOrgName),
    [presentOrgName],
  );

  const getDispatcherToken = useCallback(async () => {
    const tokenData = await api.dispatcherToken(presentCallID);
    return tokenData.token;
  }, [api, presentCallID]);

  const getCallerToken = useCallback(async () => {
    const tokenData = await api.callerToken(presentCallID);
    return tokenData.token;
  }, [api, presentCallID]);

  const endRoom = useCallback(
    async (room_name: string) => {
      return await api.endRoom(room_name);
    },
    [api],
  );

  if (!callID || !orgName) {
    return failedCallData;
  }

  return {
    getDispatcherToken,
    getCallerToken,
    endRoom,
  };
};
